import { Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import ImageUploader from "@/pages/admin/ImageUploader";
import ButtonsEditor from "@/pages/admin/ButtonsEditor";
import { CUSTOM_BLOCK_TYPES } from "@/pages/admin/schema";

const inputCls =
  "w-full bg-transparent border border-ink/20 px-2.5 py-1.5 text-sm outline-none focus:border-ink transition-colors";

let uid = 0;
const bid = () => `blk-${Date.now()}-${uid++}`;

function move(arr, from, to) {
  const a = arr.slice();
  if (to < 0 || to >= a.length) return a;
  const [it] = a.splice(from, 1);
  a.splice(to, 0, it);
  return a;
}

const newBlock = (type) => {
  const base = { id: bid(), type };
  if (type === "heading" || type === "paragraph") return { ...base, text: { ro: "", en: "" } };
  if (type === "quote") return { ...base, text: { ro: "", en: "" }, author: { ro: "", en: "" } };
  if (type === "image") return { ...base, url: "", caption: { ro: "", en: "" } };
  if (type === "fullwidth_image") return { ...base, url: "" };
  if (type === "image_text") return { ...base, url: "", heading: { ro: "", en: "" }, text: { ro: "", en: "" }, imageRight: false };
  if (type === "buttons") return { ...base, buttons: [] };
  return base;
};

export default function CustomSectionEditor({ item, onChange, lang }) {
  const blocks = item.blocks || [];
  const setBlocks = (b) => onChange({ ...item, blocks: b });
  const setBlock = (i, patch) => setBlocks(blocks.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const addBlock = (type) => setBlocks([...blocks, newBlock(type)]);

  const setBi = (i, key, v) => setBlock(i, { [key]: { ...(blocks[i][key] || {}), [lang]: v } });

  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="eyebrow text-ink/50">Nume secțiune / nav ({lang.toUpperCase()})</span>
          <input
            className={inputCls + " mt-1"}
            value={item.navLabel?.[lang] ?? ""}
            onChange={(e) => onChange({ ...item, navLabel: { ...item.navLabel, [lang]: e.target.value } })}
            data-testid="custom-navlabel"
          />
        </label>
        <label className="block">
          <span className="eyebrow text-ink/50">Temă</span>
          <select
            className={inputCls + " mt-1"}
            value={item.theme || "light"}
            onChange={(e) => onChange({ ...item, theme: e.target.value })}
            data-testid="custom-theme"
          >
            <option value="light">Deschisă</option>
            <option value="dark">Întunecată</option>
          </select>
        </label>
      </div>

      <div className="border-t border-ink/15 pt-4">
        <div className="eyebrow text-ink/60 mb-3">Blocuri de conținut</div>
        <div className="space-y-4">
          {blocks.map((b, i) => (
            <div key={b.id || i} className="border border-ink/15 p-3 bg-ink/[0.02]">
              <div className="flex items-center justify-between mb-2">
                <span className="eyebrow text-ink/50">
                  {CUSTOM_BLOCK_TYPES.find((t) => t.type === b.type)?.label || b.type}
                </span>
                <div className="flex gap-1">
                  <button type="button" onClick={() => setBlocks(move(blocks, i, i - 1))} className="p-1 hover:bg-ink/10"><ChevronUp size={13} /></button>
                  <button type="button" onClick={() => setBlocks(move(blocks, i, i + 1))} className="p-1 hover:bg-ink/10"><ChevronDown size={13} /></button>
                  <button type="button" onClick={() => setBlocks(blocks.filter((_, j) => j !== i))} className="p-1 text-red-700 hover:bg-red-700/10"><Trash2 size={13} /></button>
                </div>
              </div>

              {(b.type === "heading" || b.type === "paragraph") && (
                <textarea
                  rows={b.type === "heading" ? 1 : 3}
                  className={inputCls + " resize-y"}
                  placeholder={`Text (${lang.toUpperCase()})`}
                  value={b.text?.[lang] ?? ""}
                  onChange={(e) => setBi(i, "text", e.target.value)}
                  data-testid={`block-text-${i}`}
                />
              )}

              {b.type === "quote" && (
                <div className="space-y-2">
                  <textarea rows={2} className={inputCls + " resize-y"} placeholder={`Citat (${lang.toUpperCase()})`} value={b.text?.[lang] ?? ""} onChange={(e) => setBi(i, "text", e.target.value)} data-testid={`block-text-${i}`} />
                  <input className={inputCls} placeholder={`Autor (${lang.toUpperCase()})`} value={b.author?.[lang] ?? ""} onChange={(e) => setBi(i, "author", e.target.value)} />
                </div>
              )}

              {(b.type === "image" || b.type === "fullwidth_image") && (
                <div className="space-y-2">
                  <ImageUploader value={b.url} onChange={(v) => setBlock(i, { url: v })} testid={`block-image-${i}`} />
                  {b.type === "image" && (
                    <input className={inputCls} placeholder={`Legendă (${lang.toUpperCase()})`} value={b.caption?.[lang] ?? ""} onChange={(e) => setBi(i, "caption", e.target.value)} />
                  )}
                </div>
              )}

              {b.type === "image_text" && (
                <div className="space-y-2">
                  <ImageUploader value={b.url} onChange={(v) => setBlock(i, { url: v })} testid={`block-image-${i}`} />
                  <input className={inputCls} placeholder={`Titlu (${lang.toUpperCase()})`} value={b.heading?.[lang] ?? ""} onChange={(e) => setBi(i, "heading", e.target.value)} />
                  <textarea rows={3} className={inputCls + " resize-y"} placeholder={`Text (${lang.toUpperCase()})`} value={b.text?.[lang] ?? ""} onChange={(e) => setBi(i, "text", e.target.value)} />
                  <label className="eyebrow flex items-center gap-2 text-ink/60">
                    <input type="checkbox" checked={!!b.imageRight} onChange={(e) => setBlock(i, { imageRight: e.target.checked })} />
                    Imagine în dreapta
                  </label>
                </div>
              )}

              {b.type === "buttons" && (
                <ButtonsEditor buttons={b.buttons || []} onChange={(v) => setBlock(i, { buttons: v })} lang={lang} />
              )}
            </div>
          ))}
          {blocks.length === 0 && <div className="eyebrow text-ink/30 py-2">Niciun bloc încă.</div>}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {CUSTOM_BLOCK_TYPES.map((t) => (
            <button
              key={t.type}
              type="button"
              onClick={() => addBlock(t.type)}
              data-testid={`add-block-${t.type}`}
              className="eyebrow inline-flex items-center gap-1 border border-ink/25 px-3 py-1.5 hover:bg-ink hover:text-paper transition-colors"
            >
              <Plus size={12} /> {t.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
